import React, {
  lazy,
  Suspense,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import { Tabs } from "antd";
import { PhysioReviewContext } from "../../../Physiotherapist/PhysiotherapistReview/PhysioReviewFormProvider";
import "./PhysioScales.css";

const { TabPane } = Tabs;
const GCScale = lazy(() => import("./GCScale"));
const NIHSScale = lazy(() => import("./NIHSScale"));
const MOCAScale = lazy(() => import("./MOCAScale"));
const RASScale = lazy(() => import("./RASScale"));
const ASIAScale = lazy(() => import("./ASIAScale"));
// const VAScale = lazy(() => import("./VAScale"));
const MAScale = lazy(() => import("./MAScale"));
const MMTScale = lazy(() => import("./MMTScale"));
const ROMScale = lazy(() => import("./ROMScale"));
const VCGScale = lazy(() => import("./VCGScale"));
const RPEScale = lazy(() => import("./RPEScale"));
// const Reflexes = lazy(() => import("./Reflexes"));
const PeakFlowMeterTest = lazy(() => import("./PeakFlowMeterTest"));
const SixMinuteWalkTest = lazy(() => import("./SixMinuteWalkTest"));
const Coordination = lazy(() => import("./Coordination"));
const TCMScale = lazy(() => import("./TCMScale"));
const FingerGripTest = lazy(() => import("./FingerGripTest"));
const BarthelIndex = lazy(() => import("./BarthelIndex"));

const PhysioScalesOp = forwardRef(
  (
    {
      admission,
      patientData,
      onScalesSubmit,
      scalesId,
      setScalesId,
      scaleTab,
      setScaleTab,
      setActiveTab,
      setVisible,
      onClose,
      activeScaleKey,
      editData,
      notifyOnSave = false,
      isNeuro = false,
      onScaleDirty,
    },
    ref
  ) => {
    const [latestScaleData, setLatestScaleData] = useState(null);
    const handleScaleSubmitProxy = (...args) => {
      let data = null;
      const isExplicitSubmit =
        args.length >= 2 &&
        typeof args[0] === "string" &&
        args[1] &&
        typeof args[1] === "object";
      if (
        args.length >= 2 &&
        typeof args[0] === "string" &&
        args[1] &&
        typeof args[1] === "object"
      ) {
        data = args[1];
      } else if (args[0] && typeof args[0] === "object") {
        data = args[0];
      }
      if (!data || typeof data !== "object") return;
      setLatestScaleData(data);
      onScaleDirty?.();
      if (notifyOnSave && isExplicitSubmit && typeof onScalesSubmit === "function") {
        return onScalesSubmit(...args);
      }
    };

    useImperativeHandle(ref, () => ({
      saveScale: async () => {
        if (!latestScaleData || Object.keys(latestScaleData).length === 0) {
          return null;
        }

        if (activeScaleKey === "BarthelIndex") {
          const s = latestScaleData || {};
          const score =
            Number(s?.feeding || 0) +
            Number(s?.bathing || 0) +
            Number(s?.grooming || 0) +
            Number(s?.dressing || 0) +
            Number(s?.bowels || 0) +
            Number(s?.bladder || 0) +
            Number(s?.toilet_use || 0) +
            Number(s?.transfers || 0) +
            Number(s?.mobility || 0) +
            Number(s?.stairs || 0);

          const interpretation =
            score < 20
              ? "Totally Dependent"
              : score < 40
              ? "Very Dependent"
              : score < 60
              ? "Partially Dependent"
              : score < 80
              ? "Minimally Dependent"
              : "Independent";

          latestScaleData.score = score;
          latestScaleData.interpretation = interpretation;
        }

        if (notifyOnSave && onScalesSubmit && activeScaleKey) {
          const ok = await onScalesSubmit(activeScaleKey, latestScaleData);
          if (ok === false) return null;
        }

        return latestScaleData;
      },
    }));

    if (activeScaleKey) {
      const commonProps = {
        admission,
        onScalesSubmit: notifyOnSave ? handleScaleSubmitProxy : onScalesSubmit,
        onScaleChange: (data) => {
          if (!data || typeof data !== "object" || Object.keys(data).length === 0)
            return;
          setLatestScaleData((prev) =>
            prev && typeof prev === "object" && !Array.isArray(prev)
              ? { ...prev, ...data }
              : { ...data }
          );
          onScaleDirty?.();
        },
        scalesId,
        setScalesId,
        setScaleTab,
        setVisible,
        onClose,
        editData,
        op: true,
      };
      const renderScale = () => {
        switch (activeScaleKey) {
          case "GCS":
            return <GCScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "NIHSS":
            return <NIHSScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "MOCA":
            return (
              <MOCAScale
                {...commonProps}
                hideLastStepFooter={isNeuro}
                hideFooterInNeuro={isNeuro}
              />
            );
          case "RASS":
            return <RASScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "ASIA":
            return (
              <ASIAScale
                {...commonProps}
                hideFooterInNeuro={isNeuro}
                onScaleDirty={onScaleDirty}
              />
            );
          case "MAS":
            return <MAScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "MMT":
            return <MMTScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "ROM":
            return (
              <ROMScale
                key={`rom-${activeScaleKey}`}
                {...commonProps}
                hideLastStepFooter={isNeuro}
                hideFooterInNeuro={isNeuro}
              />
            );
          case "VCG":
            return <VCGScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "RPE":
            return <RPEScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "PeakFlowMeterTest":
            return (
              <PeakFlowMeterTest
                {...commonProps}
                hideFooterInNeuro={isNeuro}
                patientData={patientData}
              />
            );
          case "SixMinuteWalkTest":
            return (
              <SixMinuteWalkTest
                {...commonProps}
                hideFooterInNeuro={isNeuro}
                patientData={patientData}
              />
            );
          case "CoordinationTest":
            return (
              <Coordination {...commonProps} hideFooterInNeuro={isNeuro} />
            );
          case "TrunkControlTest":
            return <TCMScale {...commonProps} hideFooterInNeuro={isNeuro} />;
          case "FingerGripTest":
            return (
              <FingerGripTest
                {...commonProps}
                hideFooterInNeuro={isNeuro}
                patientData={patientData}
              />
            );
          case "BarthelIndex":
            return (
              <BarthelIndex {...commonProps} hideFooterInNeuro={isNeuro} />
            );
          default:
            return null;
        }
      };
      return (
        <div className="physio-scales">
          <Suspense fallback={<div>Loading Scale...</div>}>
            {renderScale()}
          </Suspense>
        </div>
      );
    }

    return (
      <div className="physio-scales">
        <Tabs
          activeKey={scaleTab}
          type="card"
          tabPosition="top"
          animated={{ tabPane: true }}
          onChange={(key) => {
            setScaleTab(key);
          }}
        >
          <TabPane tab="GCS" key="1">
            <Suspense fallback={<div>Loading GCS...</div>}>
              <GCScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                scalesId={scalesId}
                setScalesId={setScalesId}
                scaleTab={scaleTab}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="NIHSS" key="2">
            <Suspense fallback={<div>Loading NIHSS...</div>}>
              <NIHSScale
                onScalesSubmit={onScalesSubmit}
                admission={admission}
                setActiveTab={setActiveTab}
                scalesId={scalesId}
                setScalesId={setScalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="MOCA" key="3">
            <Suspense fallback={<div>Loading MOCA...</div>}>
              <MOCAScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="RASS" key="4">
            <Suspense fallback={<div>Loading RAS...</div>}>
              <RASScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                scalesId={scalesId}
                setScalesId={setScalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="ASIA" key="5">
            <Suspense fallback={<div>Loading ASIA...</div>}>
              <ASIAScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          {/* <TabPane tab="VAS" key="6">
          <Suspense fallback={<div>Loading VAS...</div>}>
            <VAScale
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={setVisible}
              op={true}
            />
          </Suspense>
        </TabPane> */}
          <TabPane tab="MAS" key="6">
            <Suspense fallback={<div>Loading MAS...</div>}>
              <MAScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="MMT" key="7">
            <Suspense fallback={<div>Loading MMT...</div>}>
              <MMTScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="ROM" key="8">
            <Suspense fallback={<div>Loading ROM...</div>}>
              <ROMScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="VCG" key="9">
            <Suspense fallback={<div>Loading VCG...</div>}>
              <VCGScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="RPE" key="10">
            <Suspense fallback={<div>Loading RPE...</div>}>
              <RPEScale
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          {/* <TabPane tab="Reflexes" key="12">
          <Suspense fallback={<div>Loading Reflexes...</div>}>
            <Reflexes
              admission={admission}
              setActiveTab={setActiveTab}
              onScalesSubmit={onScalesSubmit}
              setScalesId={setScalesId}
              scalesId={scalesId}
              setScaleTab={setScaleTab}
              setVisible={setVisible}
              op={true}
            />
          </Suspense>
        </TabPane> */}
          <TabPane tab="Peak Flow Meter Test" key="11">
            <Suspense fallback={<div>Loading Peak Flow Meter Test...</div>}>
              <PeakFlowMeterTest
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="Six Minute Walk Test" key="12">
            <Suspense fallback={<div>Loading Six Minute Walk Test...</div>}>
              <SixMinuteWalkTest
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="Coordination" key="13">
            <Suspense fallback={<div>Loading Coordination...</div>}>
              <Coordination
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="TCM" key="14">
            <Suspense fallback={<div>Loading TCM...</div>}>
              <TCMScale
                context={PhysioReviewContext}
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="Finger Grip Test" key="15">
            <Suspense fallback={<div>Loading Finger Grip Test...</div>}>
              <FingerGripTest
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
          <TabPane tab="Barthel Index" key="16">
            <Suspense fallback={<div>Loading Barthel Index...</div>}>
              <BarthelIndex
                admission={admission}
                setActiveTab={setActiveTab}
                onScalesSubmit={onScalesSubmit}
                setScalesId={setScalesId}
                scalesId={scalesId}
                setScaleTab={setScaleTab}
                setVisible={setVisible}
                onClose={onClose}
                op={true}
              />
            </Suspense>
          </TabPane>
        </Tabs>
      </div>
    );
  }
);

export default PhysioScalesOp;
